import React, { useState, useEffect } from "react";
import axios from "axios";
import { Fuel, TrendingDown, Calculator, MapPin, ExternalLink, Info, Phone, ArrowRight, ChevronRight, ChevronDown, Zap, Check, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { IconMap } from "../../App";
import { AnimationEmbed } from "../AnimationEmbed";
import { useFlexfuelOffer } from "../../hooks/useFlexfuelOffer";
import { OfferCountdown } from "../OfferCountdown";
import { CALC_TEXT_DEFAULTS } from "./flexfuelCalculatorText";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FlexfuelCalculator = ({ content }) => {
  const cfg = content?.flexfuel_calculator || {
    badge: "Conversion Flexfuel E85",
    title: "Calculez Vos Économies",
    subtitle: "Découvrez combien vous pouvez économiser chaque année en passant au bioéthanol E85",
  };
  const t = (k) => cfg[k] || CALC_TEXT_DEFAULTS[k];
  const [consumption, setConsumption] = useState(7);
  const [annualKm, setAnnualKm] = useState(15000);
  const [sp95Price, setSp95Price] = useState(1.75);
  const [e85Price, setE85Price] = useState(0.75);
  const [showResults, setShowResults] = useState(false);

  // E85 increases consumption by approximately 25%
  const e85ConsumptionIncrease = 1.25;
  const e85Consumption = consumption * e85ConsumptionIncrease;

  // Calculate costs
  const annualSP95Cost = (consumption / 100) * annualKm * sp95Price;
  const annualE85Cost = (e85Consumption / 100) * annualKm * e85Price;
  const annualSavings = annualSP95Cost - annualE85Cost;
  const monthlySavings = annualSavings / 12;
  const savingsPercent = ((annualSavings / annualSP95Cost) * 100).toFixed(0);

  const handleCalculate = () => {
    setShowResults(true);
  };

  const scrollToBooking = () => {
    const bookingSection = document.getElementById('booking');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="flexfuel-calculator" className="py-16 md:py-24 px-4 bg-[#0A0A0A]">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-4">
            {cfg.badge}
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase text-white mb-4">
            {cfg.title}
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            {cfg.subtitle}
          </p>
        </div>

        {cfg.show_animation !== false && (
          <AnimationEmbed src="/flexfuel.html" title="Conversion FlexFuel E85" testId="flexfuel-animation" className="mb-10" />
        )}

        <div className="bg-[#121212] border border-white/10 rounded-xl p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Consommation */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2" data-testid="flexfuel-label-consumption">
                {t("label_consumption")}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={consumption}
                  onChange={(e) => setConsumption(parseFloat(e.target.value) || 0)}
                  className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-3 text-white text-lg focus:border-[#FF3B30] focus:outline-none transition-colors"
                  step="0.1"
                  min="0"
                  data-testid="flexfuel-consumption"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40">L/100km</span>
              </div>
            </div>

            {/* Kilométrage annuel */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2" data-testid="flexfuel-label-km">
                {t("label_km")}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={annualKm}
                  onChange={(e) => setAnnualKm(parseInt(e.target.value) || 0)}
                  className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-3 text-white text-lg focus:border-[#FF3B30] focus:outline-none transition-colors"
                  step="1000"
                  min="0"
                  data-testid="flexfuel-km"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40">km/an</span>
              </div>
            </div>

            {/* Prix SP95 */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2" data-testid="flexfuel-label-sp95">
                {t("label_sp95")}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={sp95Price}
                  onChange={(e) => setSp95Price(parseFloat(e.target.value) || 0)}
                  className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-3 text-white text-lg focus:border-[#FF3B30] focus:outline-none transition-colors"
                  step="0.01"
                  min="0"
                  data-testid="flexfuel-sp95"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40">€/L</span>
              </div>
            </div>

            {/* Prix E85 */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2" data-testid="flexfuel-label-e85">
                {t("label_e85")}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={e85Price}
                  onChange={(e) => setE85Price(parseFloat(e.target.value) || 0)}
                  className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-4 py-3 text-white text-lg focus:border-[#FF3B30] focus:outline-none transition-colors"
                  step="0.01"
                  min="0"
                  data-testid="flexfuel-e85"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40">€/L</span>
              </div>
            </div>
          </div>

          {/* Calculate Button */}
          <div className="text-center mb-8">
            <button
              onClick={handleCalculate}
              className="bg-[#FF3B30] hover:bg-[#E6352B] text-white px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105"
              data-testid="flexfuel-calculate-btn"
            >
              {t("button_text")}
            </button>
          </div>

          {/* Results */}
          {showResults && (
            <div className="border-t border-white/10 pt-8" data-testid="flexfuel-results">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {/* Annual Savings */}
                <div className="bg-gradient-to-br from-[#FF3B30]/20 to-[#FF3B30]/5 border border-[#FF3B30]/30 rounded-xl p-6 text-center">
                  <p className="text-white/60 text-sm mb-2">{t("result_annual")}</p>
                  <p className="text-4xl md:text-5xl font-bold text-[#FF3B30]">
                    {annualSavings.toFixed(0)}€
                  </p>
                  <p className="text-green-400 text-sm mt-2">-{savingsPercent}% {t("result_percent_suffix")}</p>
                </div>

                {/* Monthly Savings */}
                <div className="bg-[#1C1C1E] border border-white/10 rounded-xl p-6 text-center">
                  <p className="text-white/60 text-sm mb-2">{t("result_monthly")}</p>
                  <p className="text-4xl md:text-5xl font-bold text-white">
                    {monthlySavings.toFixed(0)}€
                  </p>
                  <p className="text-white/40 text-sm mt-2">{t("result_monthly_suffix")}</p>
                </div>

                {/* Comparison */}
                <div className="bg-[#1C1C1E] border border-white/10 rounded-xl p-6 text-center">
                  <p className="text-white/60 text-sm mb-2">{t("result_cost")}</p>
                  <div className="flex items-center justify-center gap-4">
                    <div>
                      <p className="text-white/40 text-xs">SP95</p>
                      <p className="text-xl font-bold text-white line-through opacity-60">{annualSP95Cost.toFixed(0)}€</p>
                    </div>
                    <ChevronRight className="text-[#FF3B30]" />
                    <div>
                      <p className="text-green-400 text-xs">E85</p>
                      <p className="text-xl font-bold text-green-400">{annualE85Cost.toFixed(0)}€</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info note */}
              <div className="bg-[#1C1C1E] border border-white/10 rounded-lg p-4 mb-6">
                <p className="text-white/60 text-sm text-center" data-testid="flexfuel-note">
                  <span className="text-white/80">Note :</span> {t("note")}
                </p>
              </div>

              {/* CTA */}
              <div className="text-center">
                <button
                  onClick={scrollToBooking}
                  className="bg-white text-black hover:bg-white/90 px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105 inline-flex items-center gap-2"
                  data-testid="flexfuel-cta"
                >
                  <Zap size={20} />
                  {t("cta_text")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ============== FLEXFUEL FAQ ==============
export const FlexfuelFAQSection = ({ content }) => {
  const [openIdx, setOpenIdx] = useState(null);
  const defaultFaqs = [
    { q: "Ma voiture est-elle compatible ?", a: "La plupart des véhicules essence après 2000. Vérifiez avec notre outil ci-dessus." },
    { q: "La garantie constructeur est-elle maintenue ?", a: "Oui, la conversion ECU est réversible." },
    { q: "Combien vais-je économiser ?", a: "600 – 900 €/an en moyenne selon votre kilométrage." },
    { q: "Combien de temps dure l'intervention ?", a: "2 à 3 heures dans la journée." },
    { q: "Puis-je revenir à l'essence normale ?", a: "Oui, totalement réversible." },
  ];
  const cfg = content?.flexfuel_faq || {};
  const faqs = cfg.items && cfg.items.length ? cfg.items : defaultFaqs;
  const badge = cfg.badge || "Questions fréquentes";
  const title = cfg.title || "Flexfuel E85";

  // JSON-LD FAQPage schema for Google rich snippets
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <section
      id="flexfuel-faq"
      data-testid="flexfuel-faq-section"
      className="py-20 md:py-28 px-4 bg-[#0A0A0A]"
    >
      <script
        type="application/ld+json"
        data-testid="flexfuel-faq-jsonld"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-4">
            {badge}
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase text-white">
            {title}
          </h2>
        </div>
        <div className="space-y-3">
          {faqs.map((item, idx) => {
            const isOpen = openIdx === idx;
            return (
              <div
                key={idx}
                data-testid={`flexfuel-faq-item-${idx}`}
                className={`bg-[#121212] border rounded-lg transition-colors ${
                  isOpen ? "border-[#FF3B30]/40" : "border-white/[0.06]"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? null : idx)}
                  data-testid={`flexfuel-faq-toggle-${idx}`}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="text-white font-semibold text-base md:text-lg pr-4">
                    {item.q}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`text-[#FF3B30] shrink-0 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div
                    data-testid={`flexfuel-faq-answer-${idx}`}
                    className="px-5 pb-5 text-white/70 text-sm leading-relaxed"
                  >
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============== SERVICE CARD ==============
const ServiceCard = ({ service, onClick, isSelected }) => {
  const IconComponent = IconMap[service.icon] || Wrench;
  const offer = useFlexfuelOffer();
  // Fallback prices by name (used only if starting_price_label is not set in DB)
  const PRICE_MAP = {
    "Mise à jour calculateur": "À partir de 300 € TTC",
    "Conversion Flexfuel": offer.label,
  };
  const priceLabel = service.starting_price_label || PRICE_MAP[service.name] || "Sur devis";
  // Services with a starting price shown below "Sur devis"
  const INDICATIVE_PRICE_SERVICES = ["Entretien Complet", "Freinage & Contrôles", "Pneumatiques"];
  const showIndicativePrice = INDICATIVE_PRICE_SERVICES.includes(service.name);
  // Vacation pause — greys out the card and displays a "Reprise le X" ribbon.
  const today = new Date().toISOString().slice(0, 10);
  const isPaused = service.unavailable_until && today < service.unavailable_until;
  const resumeLabel = isPaused
    ? new Date(service.unavailable_until + "T00:00:00").toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";
  const showCountdown = /flexfuel/i.test(service.name) && !isPaused && offer.active;

  return (
    <div
      onClick={() => onClick(service)}
      data-testid={`service-card-${service.id}`}
      className={`group relative overflow-hidden bg-[#121212] border transition-all duration-500 p-8 flex flex-col justify-end h-[400px] cursor-pointer
        ${isPaused ? "opacity-60 hover:opacity-80" : ""}
        ${
          isSelected
            ? "border-[#FF3B30] shadow-[0_0_30px_rgba(255,59,48,0.3)]"
            : "border-white/10 hover:border-[#FF3B30]/50"
        }`}
    >
      {/* Background gradient on hover */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#FF3B30]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Icon (hidden while the offer countdown occupies the top of the card) */}
      {!showCountdown && (
      <div
        className={`absolute top-8 right-8 transition-colors duration-300 ${
          isSelected ? "text-[#FF3B30]" : "text-white/20 group-hover:text-[#FF3B30]"
        }`}
      >
        <IconComponent size={64} strokeWidth={1} />
      </div>
      )}

      {/* Content */}
      <div className="relative z-10">
        <h3 className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-3">
          {service.name}
        </h3>
        <p className="text-white/60 text-sm leading-relaxed mb-4">
          {service.description}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span
              data-testid={`service-card-price-${service.id}`}
              className="text-[#FF3B30] font-bold text-lg leading-tight"
            >
              {priceLabel}
            </span>
            {showIndicativePrice && (
              <span
                data-testid={`service-card-indicative-${service.id}`}
                className="text-white/50 text-xs mt-0.5"
              >
                À partir de 45 €
              </span>
            )}
          </div>
          <span className="text-white/40 text-sm">{service.duration} min</span>
        </div>
      </div>

      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute top-4 left-4 bg-[#FF3B30] p-2">
          <Check size={16} className="text-white" />
        </div>
      )}

      {showCountdown && (
        <div className={`absolute top-4 z-10 ${isSelected ? "left-16" : "left-4"}`}>
          <OfferCountdown offer={offer} short testId="service-card-countdown" className="px-3 py-1.5 text-[11px] rounded-full" />
        </div>
      )}

      {/* Pause ribbon — vacation / temporary closure */}
      {isPaused && (
        <div
          data-testid={`service-card-paused-${service.id}`}
          className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm border border-white/15 px-3 py-1.5 text-white text-[11px] uppercase tracking-wider font-bold"
        >
          Reprise le {resumeLabel}
        </div>
      )}
    </div>
  );
};

// ============== SERVICES SECTION ==============
export const ServicesSection = ({ services, selectedService, onSelectService, content }) => {
  const expertise = content?.expertise || { badge: "Nos Services", title: "Expertise & Rapidité", subtitle: "" };
  return (
    <section id="services" className="py-24 md:py-32 px-4 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-4">
            {expertise.badge}
          </p>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase text-white">
            {expertise.title}
          </h2>
          {expertise.subtitle && (
            <p className="text-white/60 text-base md:text-lg mt-4 max-w-2xl mx-auto">
              {expertise.subtitle}
            </p>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-6">
          {services.map((service) => (
            <div key={service.id} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(20%-20px)] min-w-[250px] max-w-[300px]">
              <ServiceCard
                service={service}
                onClick={onSelectService}
                isSelected={selectedService?.id === service.id}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============== SEO EDITORIAL SECTION (H1 + H2s for Google) ==============
